// Copyright 2025 Sorama B.V.

import { autoinject } from "aurelia-dependency-injection";
import { BindingEngine } from "aurelia-framework";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import { BusinessUnitRepository } from "repositories/BusinessUnitRepository";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag: (...args: unknown[]) => void;
    }
}

export type CompanyType = "Sorama" | "External" | "Unknown";

@autoinject
export class GoogleAnalytics {
    private readonly trackingId = "G-07L7Z367ZJ";
    private readonly streamId = "10121503478";
    private allowTracking = false;
    private localStorageCompanyTypeKey = "CompanyType";

    constructor(
        private readonly workflowModeService: WorkflowModeService,
        private readonly bindingEngine: BindingEngine,
        private readonly businessUnitRepository: BusinessUnitRepository,
        private readonly authentication: IAuthentication
    ) {
        this.bindingEngine.propertyObserver(this.workflowModeService, "selectedWorkflowMode").subscribe(() => {
            this.updateWorkflowMode();
        });
        this.bindingEngine.propertyObserver(this.authentication, "businessUnitId").subscribe(() => {
            this.updateCompanyType();
        });
    }

    public async initialize(): Promise<void> {
        // This is taken directly from google analytics' own example setup code
        window.dataLayer = window.dataLayer || [];
        window.gtag = GoogleAnalytics.gtag;
        GoogleAnalytics.gtag("js", new Date());

        // See if we already set the company type
        let companyType = localStorage.getItem(this.localStorageCompanyTypeKey) as CompanyType;
        // If it's already set, we assume it's correct (but still trigger it's update to be sure on the next refresh)
        if (companyType) {
            this.updateCompanyType();
        }
        // When never set, wait to get the company type from the business unit repository
        if (companyType == null) {
            companyType = await this.updateCompanyType();
        }

        // These preferences can later be enabled if the user consents to tracking
        // https://www.nickvanmaaren.nl/en/blog/4-tips-for-using-google-analytics-4-in-compliance-with-the-gdpr/
        GoogleAnalytics.gtag("config", this.trackingId, {
            anonymize_ip: !this.allowTracking,
            allow_google_signals: this.allowTracking,
            allow_ad_personalization_signals: this.allowTracking,
            debug_mode: SORAMA_DEBUG,
            user_properties: {
                CompanyType: companyType,
                WorkflowMode: this.workflowModeService.selectedWorkflowMode?.name,
            },
        });
    }

    /**
     * Updates the company type in google analytics user properties given the logged in user's business unit
     * @remarks This function's gtag statement seems to not update the analytics user properties properly, so for now only the return value and local storage are working
     * @returns The company type of the user, either "Sorama", "External" or "Unknown"
     */
    private async updateCompanyType(): Promise<CompanyType> {
        let companyType: CompanyType = "Unknown";

        if (this.authentication.isSignedIn) {
            try {
                const businessUnitName = await this.businessUnitRepository.getNameForBusinessUnit(
                    this.authentication.businessUnitId
                );
                companyType = businessUnitName.includes("Sorama") ? "Sorama" : "External";
            } catch (error) {
                console.error(
                    "Failed to get company type from business unit repository, setting company type to 'Unknown'",
                    error
                );
            }
        }

        // Save company type so on page refresh we apply it to the user property --> work retro-actively
        localStorage.setItem(this.localStorageCompanyTypeKey, companyType);

        GoogleAnalytics.gtag("config", this.trackingId, {
            user_properties: {
                CompanyType: companyType,
            },
        });

        return companyType;
    }

    /**
     * Updates the workflow mode in google analytics user properties
     * @remarks This function's gtag statement seems to not update the analytics user properties properly
     */
    private updateWorkflowMode() {
        GoogleAnalytics.gtag("config", this.trackingId, {
            user_properties: {
                WorkflowMode: this.workflowModeService.selectedWorkflowMode?.name,
            },
        });
    }

    // Kept private to prevent faulty usage, if you want to expose, please provide typed wrapper function instead
    private static gtag(...args: unknown[]) {
        // The arguments object is not actually a javascript array, so we can't use the spread syntax here
        // eslint-disable-next-line prefer-rest-params
        (window as typeof globalThis).dataLayer.push(arguments);
    }

    public static trackEvent(eventAction: string, eventParams: Record<string, string>) {
        this.gtag("event", eventAction, {
            ...eventParams,
        });
    }
}
